import { Box, Dialog, DialogContent } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import useColors from "../../api/hooks/useColors";
import { useOnboarding } from "../../contexts/OnboardingContext";
import { ONBOARDING_STEPS } from "../../utils/constants";
import ConnectToolsStep from "./steps/ConnectToolsStep";
import LearnBasicsNavigation from "./steps/LearnBasicsNavigation";
import LearnBasicsStep from "./steps/LearnBasicsStep";
import ScanCompleteStep from "./steps/ScanCompleteStep";
import ScanProgressStep from "./steps/ScanProgressStep";
import ScanStep from "./steps/ScanStep";
import WelcomeStep from "./steps/WelcomeStep";

// Step configuration mapping
const STEP_CONFIG = {
  [ONBOARDING_STEPS.WELCOME]: WelcomeStep,
  [ONBOARDING_STEPS.CONNECT_TOOLS]: ConnectToolsStep,
  [ONBOARDING_STEPS.SCAN]: ScanStep,
  [ONBOARDING_STEPS.SCAN_PROGRESS]: ScanProgressStep,
  [ONBOARDING_STEPS.SCAN_COMPLETE]: ScanCompleteStep,
  [ONBOARDING_STEPS.LEARN_BASICS]: LearnBasicsStep,
};

const OnboardingModal = () => {
  const colors = useColors();
  const location = useLocation();
  const { currentStage, isOnboardingModalOpen, closeOnboardingModal } =
    useOnboarding();

  const isOnOnboardingUrl = location.pathname.includes("onboarding");

  // Close modal when completed
  useEffect(() => {
    if (currentStage === ONBOARDING_STEPS.COMPLETED) {
      closeOnboardingModal();
    }
  }, [currentStage, closeOnboardingModal]);

  // Get the component for the current stage
  const StepComponent = useMemo(() => {
    return STEP_CONFIG[currentStage] || WelcomeStep;
  }, [currentStage]);

  // Handle close - prevent closing when on onboarding URL during LEARN_BASICS stage
  const handleClose = (event, reason) => {
    const isLearnBasicsOnOnboardingUrl =
      isOnOnboardingUrl && currentStage === ONBOARDING_STEPS.LEARN_BASICS;
    const isUserTriggered =
      reason === "backdropClick" || reason === "escapeKeyDown";

    // Prevent user from closing modal when on onboarding URL
    if (isLearnBasicsOnOnboardingUrl && isUserTriggered) {
      return;
    }

    closeOnboardingModal();
  };

  return (
    <Dialog
      open={isOnboardingModalOpen}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "10px",
          overflow: "hidden",
          maxWidth: "900px",
          maxHeight: "95vh",
          minHeight: "80vh",
          background: `linear-gradient(159deg, ${colors.primary_8percent} 0%, ${colors.primary_gradient_modal} 30%, ${colors.common_white} 60%), ${colors.common_white}`,
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <DialogContent
        sx={{
          flexGrow: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          overflowY: "auto",
          p: 5,
          pb: 0, // Remove bottom padding since buttons are outside
        }}
      >
        <StepComponent />
      </DialogContent>

      {/* Fixed navigation buttons at bottom of modal */}
      {currentStage === ONBOARDING_STEPS.LEARN_BASICS && (
        <Box
          sx={{
            p: 3,
            backgroundColor: "white",
            borderTop: `1px solid ${colors.grey_10percent}`,
          }}
        >
          <LearnBasicsNavigation />
        </Box>
      )}
    </Dialog>
  );
};

export default OnboardingModal;
